import * as _ from 'lodash';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  Component,
  EventEmitter,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Action, select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { selectEventConfig } from '../../features/event.selectors';
import {
  addEventPageBlockSuccess,
  addRegistrationSuccess,
  loadEventConfig,
  removeEventPageBlock,
  removeEventPageBlockSuccess,
  removeRegistrationSuccess, updateEventConfigPageSuccess,
  updateEventPageBlockSuccess,
  updateRegistrationSuccess
} from '../../features/event.actions';
import {
  ConfigurationSchema, RegistrationPartSchema,
} from '../../schemas/virtual-event';
import { CampaignService } from '../../../campaign/services/campaign.service';
import { Actions, ofType } from '@ngrx/effects';
import { ToastService } from '../../../toast/services/toast.service';
import { ImageService } from '../../../core/services/image.service';
import { MdbpInputComponent } from '../../../core/components/mdb-input/mdb-input.component';
import { IVideoData } from '../../models/event.model';
import {
  createCustomTheme, createCustomThemeSuccess,
  getCustomThemes, getCustomThemesSuccess, updateCustomTheme, updateCustomThemeSuccess
} from '../../../event-page/features/event-page.actions';
import { IThemeColorsModel } from '../../models/event.page.model';
import { defaultThemeColors, predefinedThemeColors, predefinedThemeNames } from '../../constants/event-page.constants';
import { allHeaderTypes } from '../../constants/event-page.constants';
import { EventPageConfig } from 'app/event-page/models/event-page-config-model';
import { ChooseImageModalService } from 'app/core/services/choose-image-modal.service';

@Component({
  selector: 'app-event-configuration-list',
  templateUrl: './event-configuration-list.component.html',
  styleUrls: ['./event-configuration-list.component.scss']
})
export class EventConfigurationListComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('name') name: MdbpInputComponent;

  @ViewChild(CdkVirtualScrollViewport)
  public viewport: CdkVirtualScrollViewport;

  @Input() public form: any = null;
  @Input() public index: number = 0;
  @Input() public eventPageId!: string;

  @Output() onEdit = new EventEmitter<any>();
  @Output() onDelete = new EventEmitter<any>();
  @Output() onUpdatePage = new EventEmitter<any>();
  @Output() onChangeEventConfigMode = new EventEmitter<any>();
  @Output() themeConfigurationScreenStatus = new EventEmitter<boolean>();

  public eventConfigData: any = null;
  public selectedTheme: IThemeColorsModel;
  public initialTheme: IThemeColorsModel;
  public selectedThemeId: string;
  public allHeaderTypes = allHeaderTypes;
  public selectedHeaderType: number = 1;

  public showEventPageThemeConfig: boolean = false;

  public position: number = 0;
  public cdkDragStartedPosition = 0;
  public cdkDragEndedPosition = 0;
  public slideSubmitted: boolean = false;

  public config: UntypedFormGroup = null;
  public registrations: UntypedFormGroup = null;
  public eventInfo: any;
  public themeAction: string;
  public prevNameValue = '';
  public prevDescValue = '';

  public existingEventPageDesc = '';

  public predefinedThemes: IThemeColorsModel[] = [
    predefinedThemeColors.earth,
    predefinedThemeColors.minimal,
    predefinedThemeColors.tiffany,
    predefinedThemeColors.holiday,
    predefinedThemeColors.ocean,
    predefinedThemeColors.purple,
    predefinedThemeColors.positive,
    predefinedThemeColors.empower,
  ];

  public allOrgThemes: IThemeColorsModel[] = []

  private subject: Subject<any> = new Subject();
  private takeUntilSubject: Subject<any> = new Subject();
  public loadEventConfig$ = this.store.pipe(
    select(selectEventConfig)
  );

  public quillInstance: any = null
  public quillSettedContent:boolean = false;
  public eventConfigDescription: any = null;
  public eventConfigDescLength: any = 0;

  public sectionCollapsed: boolean = true;

  public eventConfig = null;

  constructor(
    public fb: UntypedFormBuilder,
    public campaignService: CampaignService,
    public updates$: Actions,
    public imageService: ImageService,
    private toastService: ToastService,
    private store: Store,
    private chooseImageModalService: ChooseImageModalService,
  ) {
  }

  ngOnInit(): void {
    this.config = this.fb.group({});
    this.registrations = this.fb.group({});

    this.campaignService.generateForm(this.config, ConfigurationSchema);
    this.campaignService.generateForm(this.registrations, RegistrationPartSchema);

    this.imageService.initializeImageControl('event-image', this.config, this.takeUntilSubject, 'eventPageConfiguration');

    this.imageService.imageSubject.pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
        this.updateEventPage();
      }
    );

    this.store.dispatch({
      type: getCustomThemes.type,
    });

    this.config.get('eventTheme').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      this.updateEventPage();
    });

    this.config.get('eventTheme').valueChanges.pipe(
      filter(value => _.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      this.createNewTheme();
      this.config.get('eventTheme').setValue(this.selectedThemeId, { emitEvent: false });
    });

    this.loadEventConfig$.pipe(
      filter(eventConfig => !_.isNull(eventConfig)),
      takeUntil(this.takeUntilSubject)
    ).subscribe((eventConfig) => {
      this.eventConfigData = eventConfig;

      this.eventConfig = new EventPageConfig(eventConfig);

      this.eventInfo = this.getEventInfo(this.eventConfigData);

      if (eventConfig && eventConfig.customColorTheme && eventConfig.customColorTheme.id) {
        this.selectedThemeId = eventConfig.customColorTheme.id;
        this.selectedTheme = eventConfig.customColorTheme;
        this.selectedTheme.templateType = 10;
      } else if (eventConfig && (eventConfig.templateType || eventConfig.templateType === 0)) {
        this.selectedTheme = this.predefinedThemes[eventConfig.templateType];
        this.selectedThemeId = this.selectedTheme.id;
      }

      if (eventConfig.layout && eventConfig.layout.headerType) {
        this.selectedHeaderType = eventConfig.layout.headerType === 0 ? 1: eventConfig.layout.headerType;
        this.imageService.updateAspectRatio(this.getAspectRatio(), 'eventPageConfiguration');
      }

      this.config.get('eventTheme').setValue(this.selectedThemeId, { emitEvent: false });
      this.config.get('showSponsors').setValue(!this.eventConfigData.hideSponsors);
      this.config.get('hideDateTime').setValue(this.eventConfigData.hideDateTime);
      this.config.get('hideShareButtons').setValue(this.eventConfigData.hideShareButtons);
      this.config.get('customDateTimeLabel').setValue(this.eventConfigData.customDateTimeLabel);
      this.config.get('registrationButtonLabelRequired').setValue(this.eventConfigData.registrationButtonLabelRequired, { emitEvent: false});
      this.config.get('registrationButtonLabel').setValue(this.eventConfigData.registrationButtonLabel, { emitEvent: false});
      this.config.get('hideInPerson').setValue(this.eventConfigData.hideInPerson);
      this.config.get('hideVirtual').setValue(this.eventConfigData.hideVirtual);
      this.config.get('desc').setValue(this.eventConfigData.desc);
      this.config.get('name').setValue(this.eventConfigData.name);
      this.config.get('headerType').setValue(this.selectedHeaderType);

      this.existingEventPageDesc = this.eventConfigData.desc;
      this.prevNameValue = this.eventConfigData.name;
      this.prevDescValue = this.eventConfigData.desc;

      if (this.eventConfigData.coverImage !== '') {
        this.config.get('imageDataUrl').setValue(this.eventConfigData.coverImage);
        this.config.get('imageUrl').setValue(this.eventConfigData.coverImage);
      } else {
        this.config.get('imageDataUrl').setValue('/images/blank_2x1.png');
        this.config.get('imageUrl').setValue('/images/blank_2x1.png');
      }
    });

    this.updates$.pipe(
      ofType(
        addEventPageBlockSuccess.type,
        updateEventPageBlockSuccess.type,
        updateEventConfigPageSuccess.type,
        removeEventPageBlockSuccess.type,
        addRegistrationSuccess.type,
        updateRegistrationSuccess.type,
        removeRegistrationSuccess.type,
      ),
      takeUntil(this.takeUntilSubject)
    ).subscribe((action: Action) => {
      let message = '';
      if (action.type === addRegistrationSuccess.type) {
        message = 'Registration Button Added';
      }

      if (action.type === updateRegistrationSuccess.type) {
        message = 'Registration Button Updated';
      }

      if (action.type === removeRegistrationSuccess.type) {
        message = 'Registration Button Removed';
      }

      if (action.type === addEventPageBlockSuccess.type) {
        if(this.sectionCollapsed === true) {
          this.sectionCollapsed = false;
        }
        message = 'Section Was Added';
      }

      if (action.type === updateEventPageBlockSuccess.type) {
        if(this.sectionCollapsed === true) {
          this.sectionCollapsed = false;
        }
        message = 'Section Was Updated';
      }

      if (action.type === removeEventPageBlockSuccess.type) {
        message = 'Section Was Removed';
      }

      if (action.type === updateEventConfigPageSuccess.type) {
        message = 'Event Page Was Updated';
      }

      this.toastService.show('success', message, {delay: 2500});

      this.store.dispatch({
        type: loadEventConfig.type,
        payload: {
          id: this.eventPageId
        }
      });
    });

    this.updates$.pipe(
      ofType(
        getCustomThemesSuccess.type
      ),
      takeUntil(this.takeUntilSubject)
    ).subscribe((action: { type: string; data: any }) => {
      this.updateThemesList(action.data);
      this.updateSelectedTheme();
    });

    this.updates$.pipe(
      ofType(
        createCustomThemeSuccess.type,
        updateCustomThemeSuccess.type
      ),
      takeUntil(this.takeUntilSubject)
    ).subscribe((action: { type: string; data: any }) => {
      this.store.dispatch({
        type: getCustomThemes.type,
      });
    });

    this.updates$.pipe(
      ofType(updateEventConfigPageSuccess.type),
      takeUntil(this.takeUntilSubject)
    ).subscribe((action: Action) => {
      this.updateSelectedTheme();
    });

    this.config.get('headerType').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe((selectedHeaderType) => {
      if (this.selectedHeaderType !== selectedHeaderType) {

        this.selectedHeaderType = selectedHeaderType;
        this.imageService.updateAspectRatio(this.getAspectRatio(), 'eventPageConfiguration');
        this.updateEventPage();
      }
    });

    this.config.get('headerType').valueChanges.pipe(
      filter(value => _.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      this.config.get('headerType').setValue(this.selectedHeaderType, { emitEvent: false });
    });

    this.config.get('hideDateTime').valueChanges.pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe(() => {
      this.config.get('customDateTimeLabel').setValue('');
    });

    this.config.get('registrationButtonLabelRequired').valueChanges.pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe((value) => {
      const registrationButtonLabel = this.config.get('registrationButtonLabel')
      const text = value ? 'Register' : '';
      registrationButtonLabel.setValue(text, { emitEvent: false });
      registrationButtonLabel.clearValidators();
      const validators = value ? [Validators.required] : [];
      registrationButtonLabel.setValidators(validators);
      registrationButtonLabel.updateValueAndValidity({ emitEvent: false});
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.eventPageId.currentValue) {
      this.store.dispatch({
        type: loadEventConfig.type,
        payload: {
          id: changes.eventPageId.currentValue
        }
      });
    }
  }

  ngOnDestroy() {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();
    this.selectedTheme = this.predefinedThemes[0];
    this.selectedHeaderType = 1;
    this.subject.complete();
  }

  onKeyUp(event) {
    this.subject.next(event.target.value);
  }

  getAspectRatio(): number {
    return [2, 3, 4].includes(this.selectedHeaderType) ? 1 : 2;
  }

  updateSelectedTheme() {
    if (this.eventConfigData) {
      if (this.eventConfigData.templateType < predefinedThemeNames.length) {
        this.selectedTheme = this.predefinedThemes[this.eventConfigData.templateType];
        this.selectedThemeId = this.selectedTheme.id;
      } else if (this.eventConfigData.customColorTheme) {
        this.selectedThemeId = this.eventConfigData.customColorTheme.id;
        this.selectedTheme = this.getThemeById(this.selectedThemeId);
      }

      this.config.get('eventTheme').setValue(this.selectedThemeId, { emitEvent: false });
      this.initialTheme = { ...this.selectedTheme };
    }
  }

  onCDKDragStarted(event) {
    this.cdkDragStartedPosition = this.position;
  }

  onCDKDragEnded(event) {
    this.cdkDragEndedPosition = this.position;
  }

  dropOption(event) {
    moveItemInArray(this.eventConfigData.pageBlocks, event.previousIndex, event.currentIndex);

    this.updateEventPage();
  }

  editSlide(value) {
  }

  removeSection(index) {
    if (this.eventConfigData && this.eventConfigData.pageBlocks && this.eventConfigData.pageBlocks[index]) {

      this.store.dispatch({
        type: removeEventPageBlock.type,
        payload: {
          eventPageId: this.eventInfo.eventPageId,
          eventPageBlockId: this.eventConfigData.pageBlocks[index].id
        }
      });
    }
  }

  editSection(index) {
    if (this.eventConfigData && this.eventConfigData.pageBlocks && this.eventConfigData.pageBlocks[index]) {
      this.onChangeEventConfigMode.emit({
        mode: 'eventConfig',
        payload: {
          eventConfigData: this.eventConfigData,
          index
        }
      });
    }
  }

  moveTo(event: any) {
  }

  changeConfigMode() {
    this.onChangeEventConfigMode.emit({
      mode: 'eventConfig',
      payload: {
        eventConfigData: this.eventConfigData
      }
    });
  }

  updateEventPage() {
    this.config.updateValueAndValidity();

    if(this.config.invalid) {
      this.config.markAllAsTouched();
      return;
    }
    
    const eventThemeId = this.config.get('eventTheme').value;
    const eventTheme = this.getThemeById(eventThemeId);

    if (eventTheme && eventTheme.templateType === 10) {
      this.eventConfigData.customColorTheme = eventTheme;
      this.eventConfigData.customThemeId = eventTheme.id;
    } else {
      delete this.eventConfigData.customColorTheme;
      delete this.eventConfigData.customThemeId;
    }

    if (!this.eventConfigData.layout) {
      this.eventConfigData.layout = {
        headerType: 1
      };
    }

    this.eventConfigData.layout.headerType = this.selectedHeaderType;

    this.eventConfigData.templateType = eventTheme && eventTheme.templateType || 0;
    this.eventConfigData.hideSponsors = !this.config.get('showSponsors').value;
    this.eventConfigData.hideDateTime = this.config.get('hideDateTime').value;
    this.eventConfigData.customDateTimeLabel = this.eventConfigData.hideDateTime ? this.config.get('customDateTimeLabel').value : '';
    this.eventConfigData.hideShareButtons = this.config.get('hideShareButtons').value;
    this.eventConfigData.registrationButtonLabelRequired = this.config.get('registrationButtonLabelRequired').value;
    this.eventConfigData.registrationButtonLabel = this.eventConfigData.registrationButtonLabelRequired ? this.config.get('registrationButtonLabel').value : '';
    this.eventConfigData.name = this.config.get('name').value;
    this.eventConfigData.name = this.config.get('name').value;
    this.eventConfigData.desc = this.config.get('desc').value;
    this.eventConfigData.coverImage = this.config.get('imageUrl').value;

    this.prevNameValue = this.eventConfigData.name;
    this.prevDescValue = this.eventConfigData.desc;

    if (this.config.get('imageUrl').value === '/images/blank_2x1.png') {
      this.eventConfigData.coverImage = '';
    }

    this.existingEventPageDesc = this.eventConfigData.desc;
    this.onUpdatePage.emit(this.eventConfigData);
  }

  updateVirtualDateDisplay() {
    this.eventConfigData.hideVirtual = this.config.get('hideVirtual').value;
    this.onUpdatePage.emit(this.eventConfigData);
  }

  updateInPersonDateDisplay() {
    this.eventConfigData.hideInPerson = this.config.get('hideInPerson').value;
    this.onUpdatePage.emit(this.eventConfigData);
  }

  openChooseImageModal(type = 0) { // 0 for 2x1, 1 for 1x1
    const params = {
      start: 0,
      returnCount: 40,
      filterBy:[type]
    }

    this.campaignService.getOrganizationImages(params).pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe((imageList: any)=> {
      const list = imageList || [];
      const images = list.filter((image) => image.type === type)
      const modalRef = this.chooseImageModalService.open({ images, type });
    
      modalRef.onClose.pipe(
        takeUntil(this.takeUntilSubject)
      ).subscribe((imageUrl) =>{
        if(imageUrl) {
          this.config.get(`imageDataUrl`).setValue(imageUrl);
          this.config.get(`imageUrl`).setValue(imageUrl);
          
          this.toastService.show('success', 'Added Successfully!', {delay: 2000});
          this.updateEventPage();
        }
        this.chooseImageModalService.modalRef = null;

      })
    })
  }

  resetSlideImage() {
    this.config.get('imageFile').setValue(null);
    this.config.get('imageDataUrl').setValue('/images/blank_2x1.png');
    this.config.get('imageUrl').setValue('/images/blank_2x1.png');

    this.updateEventPage();
  }

  showPreview() {
    let link;

    if (this.eventConfigData.virtualEventInfo) {
      link = '/virtual-event/' + this.eventConfigData.orgInfo.handle + '/' + this.eventConfigData.virtualEventInfo.intid;
    }

    if (this.eventConfigData.hybridEventInfo) {
      link = '/hybrid-event/' + this.eventConfigData.orgInfo.handle + '/' + this.eventConfigData.hybridEventInfo.intid;
    }

    if (this.eventConfigData.inPersonEventInfo) {
      link = '/in-person-event/' + this.eventConfigData.orgInfo.handle + '/' + this.eventConfigData.inPersonEventInfo.intid;
    }

    window.open(link, '_blank');
  }

  getEventInfo(eventObj) {
    let eventInfo;

    if (eventObj.virtualEventInfo) {
      eventInfo = eventObj.virtualEventInfo;
    }

    if (eventObj.inPersonEventInfo) {
      eventInfo = eventObj.inPersonEventInfo;
    }

    if (eventObj.hybridEventInfo) {
      eventInfo = eventObj.hybridEventInfo;
    }

    return eventInfo;
  }

  getVideoUrl(videoData: IVideoData): string {
    let url = videoData.url;
    switch (videoData.provider) {
      case 0:
        // Other provider
        break;
      case 1:
        // Youtube provider
        url = videoData.url + '?autoplay=0&enablejsapi=1&fs=0&modestbranding=1&playsinline=1&rel=0'
        break;
      case 2:
        // Vimeo provider
        url = videoData.url + '?autoplay=0&playsinline=1&pip=0&loop=0&autopause=1'
        break;
      case 3:
        // Facebook provider
        break;
      // Other provider
      default:
    }

    return url;
  }

  identify(index, item) {
    return item.name;
  }

  createNewTheme() {
    this.themeAction = 'Create';
    this.showEventPageThemeConfig = true;
    this.initialTheme = {
      id: 'themeCreate',
      templateType: 10,
      name: 'New Theme',
      url: '',
      ...defaultThemeColors
    };

    this.themeConfigurationScreenStatus.emit(true);
  }

  createCustomThemeFromCurrent() {
    this.themeAction = 'Create';
    this.showEventPageThemeConfig = true;
    this.themeConfigurationScreenStatus.emit(true);
    this.selectedTheme = this.getThemeById(this.selectedThemeId);
    this.initialTheme = { ...this.selectedTheme };

    this.initialTheme.name = 'Custom ' + this.initialTheme.name;
  }

  editCustomTheme() {
    this.themeAction = 'Update';
    this.showEventPageThemeConfig = true;
    this.themeConfigurationScreenStatus.emit(true);
    this.selectedTheme = this.getThemeById(this.selectedThemeId);
    this.initialTheme = { ...this.selectedTheme };
  }

  deleteCustomTheme(event) {
    this.showEventPageThemeConfig = false;
    this.themeConfigurationScreenStatus.emit(false);
  }

  saveCustomTheme(payload: IThemeColorsModel) {
    if (this.themeAction === 'Create') {
      delete payload.id;

      this.store.dispatch({
        type: createCustomTheme.type,
        payload
      });
    } else {
      this.store.dispatch({
        type: updateCustomTheme.type,
        payload
      });
    }

    this.showEventPageThemeConfig = false;
    this.themeConfigurationScreenStatus.emit(false);
  }

  updateThemesList(customThemes: IThemeColorsModel[]) {
    this.allOrgThemes = [];
    this.allOrgThemes = [...this.predefinedThemes]

    customThemes.forEach((theme: IThemeColorsModel) => {
      this.allOrgThemes.push({
        ...theme,
        templateType: 10
      });
    });
  }

  getThemeById(id: string): IThemeColorsModel {
    return this.allOrgThemes.find(theme => theme.id === id);
  }

  getBlockImageUrl(blockImg = null) {
    return blockImg || '/images/org_logo_default_v2.svg';
  }

  onSaveEventPage() {
    this.updateEventPage();
  }
}
